import { Formik, Form, Field } from 'formik'
import React, { useState, useEffect, useCallback } from 'react'

import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import * as Yup from 'yup'
import { connect } from 'react-redux'
import history from 'utils/appHistory'
import { paymentApi } from 'api'
import AuthErrorBox from 'components/Auth/AuthErrorBox'
import PaymentCardsList from 'components/PaymentCardsList/PaymentCardsList'
import LoadingButton from 'components/ui/LoadingButton/LoadingButton'
import { authFail, resetAuthError, authRefreshToken } from 'store/actions'
import { t } from 'translation'

const ChangeCard = (props) => {
  const paymentInfoSchema = Yup.object().shape({
    cardHolder: Yup.string().required('CARDHOLDER_IS_REQUIRED'),
  })

  const [isLoading, setIsLoading] = useState(false)
  const [cardTouched, setCardTouched] = useState(false)
  const [success, setSuccess] = useState(false)
  const [cardError, setCardError] = useState(true)

  useEffect(() => {
    if (props.updatePayment) {
      const error = {
        message: 'payment_error',
        code: 401,
      }
      props.authFail(error)
    }
  }, [])

  const stripe = useStripe()
  const elements = useElements()

  const stripeHandleSubmit = useCallback(
    async (values) => {
      setIsLoading(true)

      try {
        let redirectPath
        const cardElement = elements.getElement(CardElement)
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: values.cardHolder,
          },
        })
        if ((props.subscribed && props.isDoctor) || !props.isPro) {
          const { result, error } = await paymentApi.updatePayment({
            cardholderName: values.cardHolder,
            paymentMethodId: paymentMethod.id,
            updatePayment: props.updatePayment,
          })

          if (error) throw Error(error?.message)

          if (props.updatePayment) {
            const cardPayment = await stripe.confirmCardPayment(result.clientSecret, {
              payment_method: paymentMethod.id,
            })

            if (cardPayment.paymentIntent?.status !== 'succeeded')
              throw Error(cardPayment.error.code)
          }
        }

        redirectPath = `/my-account`

        if (props.updatePayment) {
          await props.authRefreshToken()

          if (window.cordova) {
            return setTimeout(() => {
              window.location.hash = '#/my-account'
              location.reload()
            }, 6000)
          }

          return setTimeout(() => window.location.replace(redirectPath), 6000)
        }

        setIsLoading(false)
        setSuccess(true)

        return history.push(redirectPath)
      } catch (error) {
        console.log(
          'Error updating payment method: ',
          error?.error_description || error?.message || error
        )
        setIsLoading(false)
        setSuccess(false)
        return props.authFail(error)
      }
    },
    [cardError]
  )

  return (
    <div>
      <Formik
        initialValues={{ cardHolder: '' }}
        validationSchema={paymentInfoSchema}
        onSubmit={(values) => {
          if (cardError) return
          stripeHandleSubmit(values)
        }}
      >
        {({ errors, handleChange, submitForm, status, setStatus, touched }) => (
          <Form className="auth_block bg_color">
            <h1 className="section_title v4_mod color_mod offset_v4_mod decor_mod">
              {t('signup.PAYMENT_INFO')}
            </h1>
            {!success && (
              <AuthErrorBox
                formFields={['prescriberCode', 'email']}
                formTouched={touched}
                formErrors={errors}
                apiResponseError={props.authError || ''}
                resetApiResponseError={props.resetAuthError}
              />
            )}

            <PaymentCardsList />
            <div className="auth_block_input_w_list decor_mod offset_mod">
              <ul className="auth_block_input_list">
                <li className="auth_block_input_list_item">
                  <dl className="form_cell form_cell_v1_mod">
                    <dt className="form_cell_title form_v3_mod hline_hide_mod">
                      <label htmlFor="login_mail">{t(`signup.NAME_CARDHOLDER`)}</label>
                    </dt>
                    <dd className="form_field_wrap form_v3_mod">
                      <Field
                        className={`form_field v4_mod ${
                          errors.cardHolder ? 'error' : ''
                        }`}
                        type="text"
                        name="cardHolder"
                        id="cardHolder"
                        disabled={isLoading}
                        placeholder={t(`signup.NAME_CARDHOLDER`)}
                        onChange={(event) => (
                          props.resetAuthError(), handleChange(event)
                        )}
                      />
                      {/* <input
                        id="cardholder_name"
                        name="cardholderName"
                        type="text"
                        placeholder={t(`signup.NAME_CARDHOLDER`)}
                        className="form_field v4_mod"
                      /> */}
                    </dd>
                  </dl>
                </li>
                <li className="auth_block_input_list_item limitColClass">
                  <dl className="form_cell form_cell_v1_mod">
                    <dt className="form_cell_title form_v3_mod hline_hide_mod">
                      <label htmlFor="login_mail">{t(`signup.CREDIT_CARD_NUMBER`)}</label>
                    </dt>
                    <dd className="form_field_wrap form_v3_mod">
                      <CardElement
                        id="credit_card_number"
                        placeholder="signup.CREDIT_CARD_NUMBER"
                        className={`form_field v4_mod ${
                          (status === 'clicked' || cardTouched) && cardError
                            ? 'error'
                            : ''
                        } stripe-element`}
                        onChange={({ error }) => (
                          setCardTouched(true), setCardError(!!error)
                        )}
                        options={{
                          hideIcon: true,
                          hidePostalCode: true,
                          style: {
                            base: {
                              fontSize: '1.2rem',
                              fontFamily: 'Bariol, sans-serif',
                              fontWight: '400',
                              fontSmoothing: 'antialiased',
                              paddingBottom: '3px',
                              color: 'rgb(2,2,2)',
                              '::placeholder': {
                                color: '#818181',
                              },
                            },
                          },
                        }}
                      />
                    </dd>
                  </dl>
                </li>
              </ul>
            </div>
            <div className="btn_wrap center_lvl_mod">
              <LoadingButton
                className="btn_base_v1"
                isLoading={isLoading}
                onClick={(e) => {
                  e.preventDefault()

                  setStatus('clicked')
                  submitForm()
                }}
                disabled={success || !stripe || !elements}
              >
                {t('auth.UPDATE')}
              </LoadingButton>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

const mapStateToProps = ({ auth: { error, attributes, isDoctor, isPro } }) => ({
  authError: error,
  attributes,
  subscribed: JSON.parse(attributes?.subscribed || false),
  isDoctor,
  isPro,
  updatePayment: attributes.updatePayment,
})
const willMapDispatchToProps = {
  authFail,
  resetAuthError,
  authRefreshToken,
}

export default connect(mapStateToProps, willMapDispatchToProps)(ChangeCard)
